import { DOCUMENT } from '@angular/common';
import {
  AfterContentChecked,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DataAccessScreensizeFacade } from '@planone/bau365/data-access-screensize';
import { SWIPER_INTERACTION_EMITTER, SwiperInteractionEvent } from '@planone/shared/data-access-swiper';
import { Video } from '@planone/shared/util-interfaces';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { SwiperOptions } from 'swiper';

import { VideoSwiperZoomModalComponent } from '../video-swiper-zoom-modal/video-swiper-zoom-modal.component';

@Component({
  selector: 'planone-video-swiper',
  templateUrl: './video-swiper.component.html',
  styleUrls: ['./video-swiper.component.scss'],
})
export class VideoSwiperComponent implements OnInit, OnChanges, AfterContentChecked {
  @ViewChild('swiperSlider') swiper!: unknown;
  @Input() slides?: Video[];
  @Input() titles?: string[];
  @Input() swiperOptions: SwiperOptions = {};
  @Input() showSlideCount = true;
  @Input() showSlideNav = true;
  @Input() showSlideControl = false;
  @Input() enableZoom = true;
  @Input() moveToBack = false;
  @Input() forceAspectRatio = true;
  @Input() desktopVariant = 'default'; // Available variants: 'default', 'list', 'preview'
  @Input() mobileVariant = 'default'; // Available variants: 'default', 'cards'
  @Output() zoomChange: EventEmitter<boolean>;
  @Output() interaction: EventEmitter<SwiperInteractionEvent>;

  @ViewChild('videoElement') videoElement?: ElementRef;
  @ViewChild('referenceElement') referenceElement?: ElementRef;

  screenSize$: Observable<string | undefined>;
  slideCount?: number;
  slideVisibleCount?: number;
  slideActive?: number;
  zoomed = false;
  zoomedIndex?: number;
  limit = 0;
  slidesActive: Video[] = [];
  fullScreenZoom = false;

  maxHeight = 0;
  videosLimit = 8;
  videosOverflowing = false;
  videosExpanded = false;

  private unsubscribe: Subject<void> = new Subject<void>();

  private identifyVideoOverflow() {
    if (this.videoElement && this.referenceElement) {
      this.videosOverflowing =
        this.referenceElement.nativeElement.offsetHeight > this.videoElement.nativeElement.offsetHeight;
      this.maxHeight = this.videosExpanded
        ? this.referenceElement.nativeElement.offsetHeight
        : this.videoElement.nativeElement.offsetHeight;
    }
  }

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(SWIPER_INTERACTION_EMITTER) private swiperInteractionEmitter: BehaviorSubject<SwiperInteractionEvent>,
    public dialog: MatDialog,
    private zone: NgZone,
    screensizeFacade: DataAccessScreensizeFacade
  ) {
    this.slideCount = 0;
    this.slideActive = 1;
    this.screenSize$ = screensizeFacade.size$;
    this.zoomChange = new EventEmitter<boolean>();
    this.interaction = new EventEmitter<SwiperInteractionEvent>();
  }

  ngOnInit() {
    if (this.swiperOptions.slidesPerView && this.swiperOptions.slidesPerView !== 'auto') {
      this.slideVisibleCount = this.swiperOptions.slidesPerView;
    }

    this.sliderPreviewDrag();

    this.screenSize$.pipe(takeUntil(this.unsubscribe)).subscribe((screenSize) => {
      this.videosLimit = screenSize === 'desktop' || screenSize === 'laptop' ? 8 : screenSize === 'tablet' ? 6 : 3;
      this.fullScreenZoom = screenSize === 'phone';
    });
  }

  ngAfterContentChecked() {
    this.identifyVideoOverflow();
  }

  ngOnChanges() {
    this.identifyVideoOverflow();
  }

  @HostListener('window:resize')
  onResize() {
    this.identifyVideoOverflow();
  }

  toggle() {
    this.videosExpanded = !this.videosExpanded;
  }

  sliderNext() {
    (<any>this.swiper)?.swiperRef.slideNext(100);
  }

  sliderPrevious() {
    (<any>this.swiper)?.swiperRef.slidePrev(100);
  }

  sliderSlide(index: number) {
    (<any>this.swiper)?.swiperRef?.slideTo(index);
  }

  sliderChange(swiper: any) {
    const activeIndex = swiper[0].activeIndex;

    this.zone.run(() => {
      this.slideActive = activeIndex + 1;

      this.swiperInteractionEmitter.next({
        type: 'SWIPE_TO',
        index: activeIndex,
      });

      this.document
        .getElementById('sliderMetaUnzoomedPreviews' + this.slideActive)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    });
  }

  sliderInit() {
    this.slideCount = this.slides?.length ?? 0;
  }

  toggleZoom(zoomedIndex: number | undefined = undefined) {
    this.zoomedIndex = zoomedIndex;
    this.enableZoom ? (this.zoomed = !this.zoomed) : false;

    if (this.desktopVariant === 'list') {
      this.slideActive = (this.zoomedIndex ?? 0) + 1;
    }

    if (this.enableZoom) {
      this.openZoomModal();
    }
  }

  openZoomModal() {
    this.dialog.open(VideoSwiperZoomModalComponent, {
      width: this.fullScreenZoom ? '100vw' : '80vw',
      maxWidth: this.fullScreenZoom ? '100vw' : '80vw',
      maxHeight: 'calc(100vh - 48px)',
      data: {
        video: this.slides?.[this.slideActive ? this.slideActive - 1 : 0],
        title: this.titles?.[this.slideActive ? this.slideActive - 1 : 0],
        index: (this.slideActive ?? 1) - 1,
      },
    });

    this.interaction.emit({
      type: 'OPEN',
      index: (this.slideActive ?? 1) - 1,
    });
  }

  sliderPreviewDrag() {
    const slider = this.document.getElementById('sliderMetaUnzoomedPreviews');

    let isDown = false;
    let startX: number;
    let scrollLeft: number;

    const sliderPreviews = this.document.getElementsByClassName('slider-previews-item');

    Array.prototype.forEach.call(sliderPreviews, (parent) => {
      Array.prototype.forEach.call(parent.children, (child) => {
        child.setAttribute('draggable', false);
      });
    });

    slider?.addEventListener('mousedown', (e) => {
      isDown = true;
      slider.classList.add('active');
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    });

    slider?.addEventListener('touchstart', (e) => {
      isDown = true;
      slider.classList.add('active');
      startX = e.touches[0].pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    });

    slider?.addEventListener('mouseleave', () => {
      isDown = false;
      slider.classList.remove('active');
    });

    slider?.addEventListener('touchcancel', () => {
      isDown = false;
      slider.classList.remove('active');
    });

    slider?.addEventListener('mouseup', () => {
      isDown = false;
      slider.classList.remove('active');
    });

    slider?.addEventListener('touchend', () => {
      isDown = false;
      slider.classList.remove('active');
    });

    slider?.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 3;
      slider.scrollLeft = scrollLeft - walk;
    });

    slider?.addEventListener('touchmove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.touches[0].pageX - slider.offsetLeft;
      const walk = (x - startX) * 3;
      slider.scrollLeft = scrollLeft - walk;
    });
  }

  identifyByIndex(index: number): string {
    return `${index}`;
  }

  extractVideoId(url: string): string | undefined {
    const regexp = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/gi;
    const matches = regexp.exec(url);

    if (matches?.length) return matches[1];

    return undefined;
  }
}
